import { API, graphqlOperation } from "aws-amplify";
import {
  searchProducts as searchProductsQuery,
  productByMaterialCode,
  getProductLibProduct,
} from "../graphql/queries";
import { updateProductLibProduct } from "../graphql/mutations";
import { ProductLibProduct } from "../API";

export type ProductSortField =
  | "materialCode"
  | "name"
  | "updatedAt"
  | "packageCaseUpdatedAt";

export interface SearchProductsParams {
  brand?: string; // "" / undefined = all brands
  search?: string; // substring search over matCode + name
  page: number; // 1-based
  pageSize: number;
  sortField: ProductSortField;
  sortDirection: "asc" | "desc";
}

export interface SearchProductsResult {
  items: ProductLibProduct[];
  total: number;
}

// The PK/CS table searches material code and product name only. plSearch
// intersects this with its own allowlist, so a bad value here can never widen
// the scope — it can only narrow it.
const PKCS_SEARCH_FIELDS = ["materialCode", "name"];

// Filter shared by search + count: packaging fixed to EA, optional single brand,
// substring match over the two fields above.
const searchVars = (p: { brand?: string; search?: string }) => ({
  packaging: "EA",
  brand: p.brand || undefined,
  q: p.search?.trim() || undefined,
  searchFields: PKCS_SEARCH_FIELDS,
});

// Returns just the total count for a brand/search filter (no records fetched).
export const countProducts = async (params: {
  brand?: string;
  search?: string;
}): Promise<number> => {
  const result: any = await API.graphql(
    graphqlOperation(searchProductsQuery, {
      ...searchVars(params),
      limit: 1,
      from: 0,
    })
  );
  return result?.data?.searchProducts?.total ?? 0;
};

// Server-side product search: packaging fixed to EA, optional single brand,
// substring search over materialCode + name, server sort + offset pagination.
// plSearch sorts the whole filtered set in memory and tie-breaks on id, so pages
// stay stable and there is no result-window ceiling.
export const searchProducts = async (
  p: SearchProductsParams
): Promise<SearchProductsResult> => {
  const result: any = await API.graphql(
    graphqlOperation(searchProductsQuery, {
      ...searchVars(p),
      sortField: p.sortField,
      sortDirection: p.sortDirection,
      from: (p.page - 1) * p.pageSize,
      limit: p.pageSize,
    })
  );
  const conn = result?.data?.searchProducts;
  return { items: conn?.items ?? [], total: conn?.total ?? 0 };
};

// Exact lookup through the byMaterialCode GSI — reads DynamoDB directly, so it
// never lags the search index.
const queryByMaterialCode = async (
  materialCode: string
): Promise<ProductLibProduct[]> => {
  const result: any = await API.graphql(
    graphqlOperation(productByMaterialCode, { materialCode, limit: 20 })
  );
  return result?.data?.productByMaterialCode?.items ?? [];
};

// A GSI match is byte-exact, but matcodes reach us as the folder name the user
// dropped (groupByMatCode does not normalise case). Try the raw value first and
// only fall back to uppercase on a miss.
const lookupMaterialCode = async (
  materialCode: string
): Promise<ProductLibProduct[]> => {
  const raw = materialCode.trim();
  if (!raw) return [];
  const hit = await queryByMaterialCode(raw);
  if (hit.length > 0) return hit;
  const upper = raw.toUpperCase();
  return upper === raw ? [] : queryByMaterialCode(upper);
};

const isEA = (p: ProductLibProduct) => (p.packaging ?? "").toUpperCase() === "EA";

// Looks up a product by exact material code. Returns null when no product with
// that matcode exists — used to validate batch-upload folders. Prefers the EA row
// so the name/brand shown during validation is always the canonical one.
export const getProductByMaterialCode = async (
  materialCode: string
): Promise<ProductLibProduct | null> => {
  const items = await lookupMaterialCode(materialCode);
  return items.find(isEA) ?? items[0] ?? null;
};

// Exact lookup of a single product by its composite id (barcode_materialCode).
// The ProductLibProduct primary key is `<barcode>_<materialCode>`, so a barcode +
// matcode pair identifies exactly one row. Used to validate the barcode-first
// photo upload (ImportArea > Import Photo). Matcodes arrive as the folder name
// the user dropped and are not case-normalised, so retry with an uppercased
// matcode on a miss (mirrors lookupMaterialCode).
export const getProductById = async (
  barcode: string,
  materialCode: string
): Promise<ProductLibProduct | null> => {
  const bc = barcode.trim();
  const mc = materialCode.trim();
  if (!bc || !mc) return null;
  const ids = [`${bc}_${mc}`];
  const upper = `${bc}_${mc.toUpperCase()}`;
  if (upper !== ids[0]) ids.push(upper);
  for (const id of ids) {
    // eslint-disable-next-line no-await-in-loop
    const result: any = await API.graphql(
      graphqlOperation(getProductLibProduct, { id })
    );
    const product = result?.data?.getProductLibProduct;
    if (product) return product;
  }
  return null;
};

// Stamps packageCaseUpdatedAt = now on the EA product row of a matcode. Called
// after any PK/CS photo action (upload / update / delete) so the product record
// tracks when its photos last changed. The write lands in the byPackageCaseUpdated
// GSI immediately, which is what plSearch reads as its fresh overlay.
// EA only. Best-effort (errors swallowed) — a failed stamp must not fail an upload.
export const markPackageCaseUpdated = async (
  materialCode: string
): Promise<void> => {
  try {
    const items = await lookupMaterialCode(materialCode);
    const now = new Date().toISOString(); // AWSDateTime
    await Promise.all(
      items.filter(isEA).map((p) =>
        API.graphql(
          graphqlOperation(updateProductLibProduct, {
            input: { id: p.id, packageCaseUpdatedAt: now },
          })
        )
      )
    );
  } catch (err) {
    console.log("markPackageCaseUpdated error:", materialCode, err);
  }
};
